import type { Language } from '../i18n';
import type { ContributorIdentity, ProfileStats, Spoiler, WorkType, WorkTypeFilter } from '../types';

async function json<T>(response: Response): Promise<T> {
  if (!response.ok) throw new Error(String(response.status));
  return response.json() as Promise<T>;
}

async function fallbackPool(language: Language): Promise<Spoiler[]> {
  return json<Spoiler[]>(await fetch(`/fallback/${language}.json`));
}

export async function getSpoiler(language: Language, excluded: string[], type: WorkTypeFilter = 'all', publicId?: string): Promise<Spoiler> {
  try {
    const params = new URLSearchParams({ language });
    if (excluded.length) params.set('exclude', excluded.slice(0, 50).join(','));
    if (publicId) params.set('publicId', publicId);
    else if (type !== 'all') params.set('type', type);
    return await json<Spoiler>(await fetch(`/api/spoilers/random?${params}`));
  } catch {
    const pool = await fallbackPool(language);
    if (publicId) {
      const exact = pool.find((item) => item.publicId === publicId);
      if (exact) return exact;
      throw new Error('unavailable');
    }
    const typedPool = type === 'all' || publicId ? pool : pool.filter((item) => item.type === type);
    const available = typedPool.filter((item) => !excluded.includes(item.id));
    const source = available.length ? available : typedPool;
    if (!source.length) throw new Error('unavailable');
    return source[Math.floor(Math.random() * source.length)];
  }
}

export async function sendReaction(id: string, kind: 'hurt' | 'watch', value: string, token?: string): Promise<boolean> {
  if (!token) return false;
  try { return (await fetch(`/api/spoilers/${encodeURIComponent(id)}/react`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ kind, value, token }) })).ok; }
  catch { return false; }
}

export interface Submission { title: string; type: WorkType; language: Language; text: string; nickname?: string; turnstileToken: string; identity?: ContributorIdentity; }
export async function submitSpoiler(input: Submission): Promise<{ identity?: ContributorIdentity }> {
  return json(await fetch('/api/spoilers/submit', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(input) }));
}

export async function getProfile(identity: ContributorIdentity): Promise<ProfileStats> {
  return json(await fetch('/api/profile', { headers: { 'x-contributor-id': identity.id, 'x-contributor-token': identity.token } }));
}
